/**
 * ScanPanel - run a Legislation Scan on a document and watch findings stream in.
 *
 * Self-contained (local state + PubSub): pick which sources to scan against
 * (reusing the research SourceTree so jurisdiction indexes and data sources are
 * selected exactly as in Research), start the scan for the given target, and
 * render findings live as the engine flags them. Reusable from a draft, an
 * uploaded file, or a review-session document.
 *
 * F-LEG: every finding can be acted on and the action is saved - Apply (into
 * the draft, or into a review document's revised copy as a tracked change),
 * Fixed by hand, or Dismiss, each undoable. Past scans of the document (or of
 * the whole review) are listed under History, with how many findings were
 * handled, so a scan is never lost by starting a new one or navigating away.
 *
 * Uses the app's own design system throughout - Button, BrandLoader (the logo
 * animation), brand colour tokens, and the shared feedback-modal for messages
 * (never toasts). RTL-safe and fully translated.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiWarningLight, PiLinkSimpleLight,
  PiCheckCircleLight, PiXLight, PiClockCounterClockwiseLight,
  PiArrowLeftLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Spinner from '../shared/TailwindComponents/Spinner';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import BrandLoader from '../shared/brand/BrandLoader';
import { feedTimeLabel } from '../shared/feed/feedTime';
import {
  startDraftScan, startFileScan, startReviewDocumentScan, getScanFindings,
  getLatestScanForSource, applyScanFindingSuggestion, getScanHistory,
  setScanFindingDecision,
  ScanFinding, ScanSeverity, StartScanOptions, ScanDecision, ScanHistoryRun,
  ScanRunStatus,
} from '../../utils/scanApi';
import { useScanPubSub } from './useScanPubSub';
import SourceTree from '../Research/SourceTree';
import { toSourceSelection } from '../Research/sources';
import type { ResearchSource } from '../../store/actions/ChatAction';

export type ScanTarget =
  | { kind: 'draft'; draftUuid: string }
  | { kind: 'file'; fileUuid: string }
  | { kind: 'reviewDocument'; sessionUuid: string; documentId: number };

export interface ScanPanelProps {
  target: ScanTarget;
  jurisdictionSources?: ResearchSource[];
  userDatasources?: ResearchSource[];
  defaultSelected?: ResearchSource[];
  language?: string | null;
  /** Review scans: names for the documents a whole-review history lists. */
  documentNames?: Record<number, string>;
  onClose?: () => void;
}

type PanelStatus = 'idle' | 'starting' | 'running' | 'completed' | 'partial' | 'failed' | 'stalled';

const SEVERITY_ORDER: ScanSeverity[] = ['critical', 'major', 'minor', 'info'];

// Match the Review surface's severity tones (danger / warning / info scales).
const SEVERITY_BADGE: Record<ScanSeverity, BadgeColor> = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow',
  info: 'blue',
};

// The engine's finding taxonomy, as people read it.
const FINDING_TYPE_LABEL: Record<string, string> = {
  conflict_with_in_force_law: 'Conflicts with law in force',
  citation_to_repealed_or_superseded_law: 'Cites repealed or superseded law',
  missing_statutory_provision: 'Missing statutory provision',
  jurisdiction_mismatch: 'Jurisdiction mismatch',
  outdated_reference: 'Outdated reference',
};

const DECISION_LABEL: Record<ScanDecision, string> = {
  accepted: 'Applied',
  implemented: 'Fixed by hand',
  dismissed: 'Dismissed',
};

function severityRank(s: ScanSeverity): number {
  const i = SEVERITY_ORDER.indexOf(s);
  return i < 0 ? 99 : i;
}

/** A finished run's panel status. */
function terminalStatus(status: string): PanelStatus {
  if (status === 'failed') return 'failed';
  if (status === 'completed_with_errors') return 'partial';
  return 'completed';
}

const ScanPanel: React.FC<ScanPanelProps> = ({
  target,
  jurisdictionSources = [],
  userDatasources = [],
  defaultSelected = [],
  language = null,
  documentNames = {},
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [selected, setSelected] = useState<ResearchSource[]>(defaultSelected);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [scanUuid, setScanUuid] = useState<string | null>(null);
  const [run, setRun] = useState<ScanRunStatus | null>(null);
  const [status, setStatus] = useState<PanelStatus>('idle');
  const [findings, setFindings] = useState<ScanFinding[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyScope, setHistoryScope] = useState<'document' | 'review'>('document');
  const [history, setHistory] = useState<ScanHistoryRun[] | null>(null);

  // How this scan target is keyed on ScanRun.source_document_uuid - so we can
  // resurface a prior scan for this exact document after a refresh.
  const sourceRef =
    target.kind === 'draft' ? target.draftUuid
      : target.kind === 'file' ? target.fileUuid
        : `${target.sessionUuid}#${target.documentId}`;

  const notify = useCallback((modalType: 'success' | 'error' | 'warning', message: string) => {
    dispatch(showFeedbackModal({ showModal: true, modalType, message, duration: 6000 }));
  }, [dispatch]);

  /** Show a stored run: finished ones as they are, live ones resume polling. */
  const showRun = useCallback((scan: ScanRunStatus, prior: ScanFinding[]) => {
    setScanUuid(scan.uuid);
    setRun(scan);
    setFindings(prior || []);
    if (scan.status === 'completed' || scan.status === 'completed_with_errors' || scan.status === 'failed') {
      setStatus(terminalStatus(scan.status));
      return;
    }
    // pending/processing - resume only if it's a FRESH run. A run stuck
    // pending for >15 min is orphaned (worker crash); show it honestly as
    // stalled - a stale "processing" run is NOT a clean "no issues" result.
    const started = scan.started_at ? new Date(scan.started_at).getTime() : 0;
    const ageMin = started ? (Date.now() - started) / 60000 : Infinity;
    setStatus(ageMin <= 15 ? 'running' : 'stalled');
  }, []);

  // On open, load the latest scan for this document (survives refresh).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { scan, findings: prior } = await getLatestScanForSource(sourceRef);
        if (cancelled || !scan) return;
        showRun(scan, prior);
      } catch {
        // no prior scan / transient - stay idle
      }
    })();
    return () => { cancelled = true; };
  }, [sourceRef, showRun]);

  const handleFinding = useCallback((f: ScanFinding) => {
    setFindings((prev) => (prev.some((p) => p.uuid === f.uuid) ? prev : [...prev, f]));
  }, []);

  const handleCompleted = useCallback((payload: { status: string }) => {
    setStatus(terminalStatus(payload.status));
  }, []);

  // Live path: PubSub (fast). Runs when the socket connects.
  useScanPubSub({
    scanUuid,
    enabled: !!scanUuid && (status === 'running' || status === 'starting'),
    onFinding: handleFinding,
    onCompleted: handleCompleted,
  });

  // Reliable fallback: poll the read API. Findings + status are persisted by the
  // engine regardless of PubSub, so this guarantees the panel shows results and
  // completion even if the socket never connects. Stops once terminal.
  useEffect(() => {
    if (!scanUuid || !(status === 'running' || status === 'starting')) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~4 min at 4s - backstop so a stuck run can't loop forever
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      attempts += 1;
      try {
        const { scan, findings: fetched } = await getScanFindings(scanUuid);
        if (cancelled) return;
        if (fetched?.length) {
          // Only push genuinely new findings, and only replace state when the
          // set actually grew - returning `prev` unchanged avoids a re-render on
          // every 4s tick when nothing new has landed.
          setFindings((prev) => {
            const seen = new Set(prev.map((p) => p.uuid));
            const added = fetched.filter((f) => !seen.has(f.uuid));
            return added.length ? [...prev, ...added] : prev;
          });
        }
        if (scan?.status === 'completed' || scan?.status === 'completed_with_errors' || scan?.status === 'failed') {
          setRun(scan);
          const next = terminalStatus(scan.status);
          setStatus((s) => (s === next ? s : next));
          return; // terminal - stop polling
        }
      } catch {
        // transient - keep polling
      }
      if (!cancelled && attempts < MAX_ATTEMPTS) {
        timer = setTimeout(poll, 4000);
      } else if (!cancelled) {
        // Gave up before the run reached a terminal state - it's stalled, not
        // clean. Don't leave an infinite spinner or imply "no issues".
        setStatus('stalled');
      }
    };
    timer = setTimeout(poll, 800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [scanUuid, status]);

  // History - fetched when opened, and again when its scope changes.
  useEffect(() => {
    if (!showHistory) return;
    let cancelled = false;
    setHistory(null);
    const scope = historyScope === 'review' && target.kind === 'reviewDocument'
      ? { review: target.sessionUuid }
      : { source: sourceRef };
    getScanHistory(scope)
      .then((runs) => { if (!cancelled) setHistory(runs); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [showHistory, historyScope, sourceRef, target]);

  const openRun = useCallback(async (r: ScanHistoryRun) => {
    try {
      const { scan, findings: fetched } = await getScanFindings(r.uuid);
      showRun(scan, fetched);
      setShowHistory(false);
    } catch {
      notify('error', t('Could not open that scan. Please try again.'));
    }
  }, [showRun, notify, t]);

  const hasSources = jurisdictionSources.length > 0 || userDatasources.length > 0;

  const buildOptions = (): StartScanOptions => ({
    selected_sources: toSourceSelection(selected),
    language,
  });

  const startScan = async () => {
    if (selected.length === 0) {
      notify('warning', t('Select at least one source to scan against'));
      return;
    }
    setStatus('starting');
    setFindings([]);
    setRun(null);
    try {
      const opts = buildOptions();
      let result;
      if (target.kind === 'draft') result = await startDraftScan(target.draftUuid, opts);
      else if (target.kind === 'file') result = await startFileScan(target.fileUuid, opts);
      else result = await startReviewDocumentScan(target.sessionUuid, target.documentId, opts);

      if (result.error || !result.scan_run_uuid) {
        setStatus('failed');
        notify('error', result.error || t('Could not start the scan'));
        return;
      }
      setScanUuid(result.scan_run_uuid);
      setStatus('running');
    } catch (e: any) {
      setStatus('failed');
      notify('error', e?.response?.data?.error || t('Could not start the scan'));
    }
  };

  const sortedFindings = useMemo(
    () => [...findings].sort((a, b) => {
      // Dismissed findings sink below the ones still to look at.
      const d = Number(a.decision === 'dismissed') - Number(b.decision === 'dismissed');
      if (d !== 0) return d;
      const r = severityRank(a.severity) - severityRank(b.severity);
      if (r !== 0) return r;
      return (b.risk_score || 0) - (a.risk_score || 0);
    }),
    [findings],
  );

  const counts = useMemo(() => {
    const c: Record<ScanSeverity, number> = { critical: 0, major: 0, minor: 0, info: 0 };
    findings.forEach((f) => { if (c[f.severity] !== undefined) c[f.severity] += 1; });
    return c;
  }, [findings]);

  const handledCount = useMemo(() => findings.filter((f) => !!f.decision).length, [findings]);

  const isRunning = status === 'running' || status === 'starting';
  const isFinished = status === 'completed' || status === 'partial';

  // Return to the source-selection screen so a new scan can be started. A scan
  // can be run as many times as the user wants - earlier runs stay in History.
  const startNewScan = useCallback(() => {
    setScanUuid(null);
    setRun(null);
    setFindings([]);
    setStatus('idle');
    setShowHistory(false);
  }, []);

  const copySuggestion = useCallback((text: string) => {
    try { navigator.clipboard?.writeText(text); notify('success', t('Suggested change copied')); }
    catch { notify('warning', t('Could not copy — select and copy manually')); }
  }, [notify, t]);

  const patchFinding = useCallback((uuid: string, patch: Partial<ScanFinding>) => {
    setFindings((prev) => prev.map((f) => (f.uuid === uuid ? { ...f, ...patch } : f)));
  }, []);

  // Apply a finding's suggested redline into the draft. Server-side, saved as a
  // NEW (restorable) version - never a destructive overwrite. On a stale
  // finding the backend reports applied=false.
  const applyToDraft = useCallback(async (f: ScanFinding) => {
    if (!scanUuid) return;
    setBusyId(f.uuid);
    try {
      const res = await applyScanFindingSuggestion(scanUuid, f.uuid);
      if (res.applied) {
        patchFinding(f.uuid, { decision: 'accepted' });
        notify('success', t('Change applied to the draft (a new version was saved)'));
      } else if (res.reason === 'flagged_text_not_found') {
        notify('warning', t('The document has changed since this scan — re-scan, then apply.'));
      } else {
        notify('warning', t('Nothing to apply for this finding.'));
      }
    } catch {
      notify('error', t('Could not apply the change. Please try again.'));
    } finally {
      setBusyId(null);
    }
  }, [scanUuid, notify, patchFinding, t]);

  // Every other decision, including Apply on a review document (the backend
  // rebuilds the review's revised copy with the change tracked).
  const decide = useCallback(async (f: ScanFinding, decision: ScanDecision | null) => {
    if (!scanUuid) return;
    const before = f.decision ?? null;
    setBusyId(f.uuid);
    patchFinding(f.uuid, { decision });
    try {
      const res = await setScanFindingDecision(scanUuid, f.uuid, decision);
      if (decision === 'accepted') {
        notify(
          res.revision?.revision_available ? 'success' : 'warning',
          res.revision?.revision_available
            ? t('Change added to the revised document')
            : t('Marked as applied, but the clause could not be found in the document to change it.'),
        );
      }
    } catch {
      patchFinding(f.uuid, { decision: before });
      notify('error', t('Could not save that. Please try again.'));
    } finally {
      setBusyId(null);
    }
  }, [scanUuid, notify, patchFinding, t]);

  const runTime = run?.completed_at || run?.started_at || undefined;
  const runTimeLabel = runTime ? feedTimeLabel(runTime, t) : null;

  const renderHistory = () => (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-4 py-2 border-b border-hairline-soft flex items-center gap-2 text-sm">
        <Button
          variant="neutral"
          size="small"
          onClick={() => setShowHistory(false)}
          ariaLabel={t('Back')}
          icon={<PiArrowLeftLight size={16} className="rtl:rotate-180" />}
        />
        <span className="font-medium text-fg-strong">{t('Scan history')}</span>
        {target.kind === 'reviewDocument' && (
          <span className="ms-auto flex items-center gap-1" role="group" aria-label={t('Scan history')}>
            {(['document', 'review'] as const).map((scope) => (
              <button
                key={scope}
                type="button"
                onClick={() => setHistoryScope(scope)}
                aria-pressed={historyScope === scope}
                className={`nav-pill text-xs ${historyScope === scope ? 'nav-pill-active' : ''}`}
              >
                {scope === 'document' ? t('This document') : t('Whole review')}
              </button>
            ))}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {history === null && <Spinner size="sm" centered ariaLabel={t('Loading…')} />}
        {history?.length === 0 && (
          <p className="text-sm text-fg-muted">{t('No earlier scans.')}</p>
        )}
        {history?.map((r) => {
          const when = feedTimeLabel(r.completed_at || r.started_at || undefined, t);
          const handled = r.decisions.accepted + r.decisions.implemented + r.decisions.dismissed;
          const docName = r.document_id != null ? documentNames[r.document_id] : undefined;
          return (
            <button
              key={r.uuid}
              type="button"
              onClick={() => openRun(r)}
              className={`w-full text-start rounded-lg border bg-surface p-3 shadow-sm hover:border-champagne transition-colors ${r.uuid === scanUuid ? 'border-champagne' : 'border-hairline-soft'}`}
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="text-fg-strong font-medium" title={when.title}>{when.label}</span>
                {r.status === 'failed' && <Badge color="red">{t('Failed')}</Badge>}
                {r.status === 'completed_with_errors' && <Badge color="yellow">{t('Partly checked')}</Badge>}
                <span className="ms-auto text-xs text-fg-muted">
                  {t('{{handled}} of {{total}} handled', { handled, total: r.counts.total })}
                </span>
              </div>
              {historyScope === 'review' && docName && (
                <div className="text-xs text-fg-muted truncate mt-0.5">{docName}</div>
              )}
              <div className="flex items-center gap-1.5 mt-1">
                {SEVERITY_ORDER.map((s) => r.counts[s] > 0 && (
                  <Badge key={s} color={SEVERITY_BADGE[s]}>{r.counts[s]} {t(s)}</Badge>
                ))}
                {r.counts.total === 0 && r.status !== 'failed' && (
                  <span className="text-xs text-fg-muted">{t('No issues were flagged.')}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderActions = (f: ScanFinding) => {
    const busy = busyId === f.uuid;
    if (f.decision) {
      // A draft change is a saved version - undone from version history, not here.
      const canUndo = !(f.decision === 'accepted' && target.kind === 'draft');
      return (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-fg-muted inline-flex items-center gap-1">
            <PiCheckCircleLight size={14} className={f.decision === 'dismissed' ? '' : 'text-success-700'} />
            {t(DECISION_LABEL[f.decision])}
          </span>
          {canUndo && (
            <Button variant="neutral" size="small" onClick={() => decide(f, null)} isLoading={busy}>
              {t('Undo')}
            </Button>
          )}
        </div>
      );
    }
    const canApply = !!f.suggested_redline && target.kind !== 'file';
    return (
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {canApply && (
          <Button
            variant="primary"
            size="small"
            onClick={() => (target.kind === 'draft' ? applyToDraft(f) : decide(f, 'accepted'))}
            isLoading={busy}
          >
            {target.kind === 'draft' ? t('Apply to draft') : t('Apply to revision')}
          </Button>
        )}
        <Button variant="outline" size="small" onClick={() => decide(f, 'implemented')} disabled={busy}>
          {t('Fixed by hand')}
        </Button>
        <Button variant="neutral" size="small" onClick={() => decide(f, 'dismissed')} disabled={busy}>
          {t('Dismiss')}
        </Button>
      </div>
    );
  };

  return (
    /* Grey glass, not a white sheet: the side panel is the glass, this tints
       it, and each finding floats on it as a solid white card. */
    <div className="flex flex-col h-full bg-surface-sunken/40 text-fg-body text-start" dir="auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
        <h2 className="text-base font-medium text-fg-strong">{t('Legislation scan')}</h2>
        <div className="flex items-center gap-2">
          {!isRunning && !showHistory && (
            <Button
              variant="neutral"
              size="small"
              onClick={() => setShowHistory(true)}
              icon={<PiClockCounterClockwiseLight size={16} />}
            >
              {t('History')}
            </Button>
          )}
          {!isRunning && status !== 'idle' && (
            <Button variant="outline" size="small" onClick={startNewScan}>
              {t('New scan')}
            </Button>
          )}
          {onClose && (
            <Button
              variant="neutral"
              size="small"
              onClick={onClose}
              ariaLabel={t('Close')}
              icon={<PiXLight size={18} />}
            />
          )}
        </div>
      </div>

      {showHistory && renderHistory()}

      {/* Source selection */}
      {!showHistory && status === 'idle' && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="px-4 py-3">
            <p className="text-sm text-fg-muted">
              {t('Scan this document against legislation and flag conflicts, repealed references, and jurisdiction mismatches.')}
            </p>
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            {hasSources ? (
              <SourceTree
                jurisdictionSources={jurisdictionSources}
                userDatasources={userDatasources}
                selected={selected}
                onChange={setSelected}
                expanded={expanded}
                onToggleExpanded={(code) =>
                  setExpanded((prev) => ({ ...prev, [code]: prev[code] === false }))
                }
              />
            ) : (
              <p className="text-sm text-warning-900 dark:text-warning-200 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800/50 rounded-lg p-3">
                {t('No scan sources are configured. Add a jurisdiction index or a data source first.')}
              </p>
            )}
          </div>
          <div className="px-4 py-3 border-t border-hairline-soft">
            <Button
              variant="primary"
              size="medium"
              onClick={startScan}
              disabled={selected.length === 0}
              className="w-full justify-center"
            >
              {t('Run scan')}
            </Button>
          </div>
        </div>
      )}

      {/* Running / results */}
      {!showHistory && status !== 'idle' && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="px-4 py-2 border-b border-hairline-soft flex items-center gap-3 text-sm">
            {isRunning ? (
              <span className="flex items-center gap-2 text-fg-body">
                <BrandLoader size="glyph" tone="brand" />
                {t('Scanning…')}
              </span>
            ) : status === 'completed' ? (
              <span className="flex items-center gap-2 text-success-700">
                <PiCheckCircleLight size={18} />
                {t('Scan complete')}
              </span>
            ) : status === 'partial' ? (
              <span className="flex items-center gap-2 text-warning-600">
                <PiWarningLight size={18} />
                {t('Some sections couldn’t be checked')}
              </span>
            ) : status === 'stalled' ? (
              <span className="flex items-center gap-2 text-warning-600">
                <PiWarningLight size={18} />
                {t('Scan didn’t finish')}
              </span>
            ) : (
              <span className="flex items-center gap-2 text-danger-600">
                <PiWarningLight size={18} />
                {t('Scan failed')}
              </span>
            )}
            {runTimeLabel && !isRunning && (
              <span className="text-xs text-fg-muted" title={runTimeLabel.title}>{runTimeLabel.label}</span>
            )}
            <span className="ms-auto flex items-center gap-1.5">
              {SEVERITY_ORDER.map((s) => counts[s] > 0 && (
                <Badge key={s} color={SEVERITY_BADGE[s]}>
                  {counts[s]} {t(s)}
                </Badge>
              ))}
            </span>
          </div>
          {isFinished && findings.length > 0 && (
            <div className="px-4 pt-2 text-xs text-fg-muted" data-testid="scan-handled">
              {t('{{handled}} of {{total}} handled', { handled: handledCount, total: findings.length })}
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {sortedFindings.length === 0 && isRunning && (
              <div className="flex h-full min-h-[16rem] flex-col items-center justify-center text-center">
                <BrandLoader size="lg" tone="brand" label={t('Scanning…')} />
                <p className="mt-3 text-sm text-fg-muted">{t('Findings appear here as each document is read.')}</p>
              </div>
            )}
            {sortedFindings.length === 0 && isFinished && (
              <p className="text-sm text-fg-muted">{t('No issues were flagged.')}</p>
            )}
            {sortedFindings.length === 0 && status === 'stalled' && (
              <div className="flex flex-col items-start gap-2 py-4">
                <p className="text-sm text-fg-muted">
                  {t('This scan didn’t finish — the run may have stalled. Start a new scan to try again.')}
                </p>
                <Button variant="outline" size="small" onClick={startNewScan}>
                  {t('Run scan again')}
                </Button>
              </div>
            )}
            {sortedFindings.length === 0 && status === 'failed' && (
              <p className="text-sm text-danger-600">{t('The scan failed to run. Please try again.')}</p>
            )}
            {sortedFindings.map((f) => {
              const leg = f.legislation || f.legislation_cited || {};
              return (
                <div
                  key={f.uuid}
                  data-testid="scan-finding"
                  className={`rounded-lg border border-hairline-soft bg-surface p-3 shadow-sm ${f.decision === 'dismissed' ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge color={SEVERITY_BADGE[f.severity]}>{t(f.severity)}</Badge>
                    {f.finding_type && (
                      <span className="text-xs text-fg-muted">
                        {t(FINDING_TYPE_LABEL[f.finding_type] || f.finding_type)}
                      </span>
                    )}
                    {f.section_reference && (
                      <span className="text-xs text-fg-muted ms-auto">{f.section_reference}</span>
                    )}
                  </div>
                  {f.issue_description && (
                    <p className="text-sm text-fg-body mb-1">{f.issue_description}</p>
                  )}
                  {f.flagged_text_span && (
                    <blockquote className="text-xs text-fg-muted border-s-2 border-hairline ps-2 my-1">
                      {f.flagged_text_span}
                    </blockquote>
                  )}
                  {f.rationale && (
                    <details className="text-xs text-fg-muted my-1">
                      <summary className="cursor-pointer select-none">{t('Legal reasoning')}</summary>
                      <p className="mt-1 whitespace-pre-wrap">{f.rationale}</p>
                    </details>
                  )}
                  {f.suggested_redline && (
                    <div className="mt-2 rounded-md border border-success-200 dark:border-success-800/50 bg-success-50/60 dark:bg-success-900/20 p-2">
                      <div className="flex items-center text-[11px] font-medium text-success-700 dark:text-success-300 mb-1">
                        {t('Suggested change')}
                        <button
                          type="button"
                          onClick={() => copySuggestion(f.suggested_redline || '')}
                          className="ms-auto text-fg-muted hover:text-fg-strong font-normal"
                        >
                          {t('Copy')}
                        </button>
                      </div>
                      <p className="text-xs text-fg-body whitespace-pre-wrap">{f.suggested_redline}</p>
                    </div>
                  )}
                  {(leg.title || leg.document_number) && (
                    <div className="text-xs text-fg-muted mt-1 flex items-center gap-1 flex-wrap">
                      <span>{leg.title}{leg.document_number ? ` (${leg.document_number})` : ''}</span>
                      {leg.in_force === false && (
                        <span className="px-1 rounded bg-danger-50 text-danger-700 border border-danger-200">{t('repealed / superseded')}</span>
                      )}
                      {leg.in_force === true && (
                        <span className="px-1 rounded bg-success-50 text-success-700 border border-success-200">{t('in force')}</span>
                      )}
                      {leg.external_url && /^https?:\/\//i.test(leg.external_url) && (
                        <a href={leg.external_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-0.5">
                          <PiLinkSimpleLight size={12} />{t('source')}
                        </a>
                      )}
                    </div>
                  )}
                  {isFinished && renderActions(f)}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanPanel;
